<script setup lang="ts">
import IconChevronDown from '~icons/mdi/chevron-down'
import IconChevronUp from '~icons/mdi/chevron-up'
import IconExternalLink from '~icons/mdi/open-in-new'

const props = withDefaults(defineProps<{
  modelValue: boolean
  alternativeProducts: any | null
  /** Which alternatives to fetch: GPO-only (cart recommendations, the default) or all products. */
  scope?: 'gpo' | 'all'
}>(), {
  scope: 'gpo',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'alternativeAdded': []
}>()

const cartStore = useCart()
provide('isAlternativeSheet', true)

const alternativeStore = useProductAlternativesStore()

const localePath = useLocalePath()
const { getMatchingCriteriaData } = useProductAlternativesHelper()
const { searchRouteName } = useSearchRoute()

const customSourceUrl = computed(() => {
  if (props.alternativeProducts?.idExternal && props.alternativeProducts?.supplierHandle) {
    const queryParams = new URLSearchParams({
      idExternal: props.alternativeProducts.idExternal,
      s: props.alternativeProducts.supplierHandle,
    })
    return `${window.location}/alternatives?${queryParams.toString()}`
  }
  return null
})

provide('alternativeCustomSourceUrl', customSourceUrl)

const expandedIndices = ref<number[]>([0])
const panels = ref<any[]>([])
const stickyBanner = ref<InstanceType<typeof import('vuetify/components').VBanner> | null>(null)

const initialCartItemCount = ref(0)

watch(expandedIndices, async (newVal, oldVal) => {
  const expandedIndex = newVal.find(id => !oldVal?.includes(id))
  if (expandedIndex !== undefined) {
    await nextTick()
    setTimeout(() => {
      const panel = panels.value[expandedIndex]
      if (panel?.$el) {
        const bannerHeight = stickyBanner.value?.$el?.offsetHeight ?? 0
        panel.$el.style.scrollMarginTop = `${bannerHeight}px`
        panel.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 400)
  }
})

watch(() => props.alternativeProducts, () => {
  if (props.alternativeProducts?.handle) {
    expandedIndices.value = [0]
    alternativeStore.fetchAlternatives(
      props.alternativeProducts.handle,
      props.scope,
      props.alternativeProducts.sourceGroupName,
    )
  }
}, { immediate: true })

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const alternatives = computed(() => alternativeStore.alternatives ?? [])

const groupOffsets = computed<number[]>(() => {
  const offsets: number[] = []
  let running = 0
  for (const group of alternatives.value) {
    offsets.push(running)
    running += group.productGroups?.length ?? 0
  }
  return offsets
})

const cartItemCount = computed(() => cartStore.carts.reduce((acc, cart) => acc + cart.items.length, 0))

watch(isOpen, (newVal) => {
  if (newVal) {
    initialCartItemCount.value = cartItemCount.value
  }
  else if (!newVal && props.alternativeProducts?.handle) {
    cartStore.setSelectedProduct(props.alternativeProducts?.idExternal)
  }
})

function handleCloseSheet() {
  // Check if any new items were added (comparing current count vs initial count)
  const hasAddedProducts = cartItemCount.value > initialCartItemCount.value

  isOpen.value = false

  if (hasAddedProducts) {
    emit('alternativeAdded')
  }
}
</script>

<template>
  <v-bottom-sheet
    v-model="isOpen"
    max-height="80vh"
    class="overflow-y-auto"
    data-test-id="alternatives-sheet"
    @click:outside="handleCloseSheet"
  >
    <v-sheet>
      <v-banner ref="stickyBanner" sticky style="z-index: 999">
        <v-btn
          data-test-id="alternatives-sheet-close-button"
          data-track-id="alternatives_sheet_close"
          icon
          variant="text"
          class="position-absolute"
          style="top: 16px; right: 16px;"
          @click="handleCloseSheet"
        >
          <v-icon>veticon-close</v-icon>
        </v-btn>
        <v-row class="align-center px-4">
          <v-col cols="12" sm="2" md="1">
            <ProductImage
              :src="props.alternativeProducts?.image"
              :fallback-src="props.alternativeProducts?.supplierImage"
              :height="80"
              :width="80"
              :alt-label="props.alternativeProducts?.name"
            />
          </v-col>
          <v-col cols="12" md="6">
            <p class="text-h6 font-weight-bold">
              {{ props.alternativeProducts?.name }}
            </p>
            <div v-if="props.alternativeProducts?.atcvetCodes.length > 0">
              <strong class="mr-1">
                {{ $t('cart.alternatives.atcvetcode') }}
              </strong>
              <template v-for="code in props.alternativeProducts?.atcvetCodes" :key="code">
                <NuxtLink
                  :data-test-id="`alternatives-atcvet-link-${code}`"
                  :to="{ path: localePath(searchRouteName), query: { q: code } }"
                  data-nav-id="alternatives_atcvet_search"
                  class="d-inline-flex align-center"
                  target="_blank"
                >
                  {{ code }}
                  <IconExternalLink class="ml-1 " />
                </NuxtLink>
              </template>
            </div>
            <div v-if="alternativeProducts.admissionSpecies.length > 0">
              <strong class="mr-1">
                {{ $t('cart.alternatives.species') }}
              </strong>
              <span>
                {{ alternativeProducts.admissionSpecies.map((i: string) => $t(i)).join(', ') || '-' }}
              </span>
            </div>
            <div v-if="alternativeProducts?.dosageForms.length > 0">
              <strong class="mr-1">
                {{ $t('cart.alternatives.dosageForms') }}
              </strong>
              <span>
                {{ alternativeProducts?.dosageForms.map((i: string) => $t(i)).join(', ') || '-' }}
              </span>
            </div>
          </v-col>
          <v-col cols="0" class="d-none d-md-block" md="4">
            <v-alert
              v-if="alternatives.length > 0"
              type="warning"
              variant="tonal"
              prominent
              class="alternatives-disclaimer"
            >
              <div>
                {{ $t('cart.alternatives.disclaimer.title') }}&nbsp;<strong>&#9432;</strong>
              </div>
              <v-tooltip activator="parent" location="bottom" max-width="450">
                {{ $t('cart.alternatives.disclaimer.description') }}
              </v-tooltip>
            </v-alert>
          </v-col>
        </v-row>
      </v-banner>
      <v-alert
        v-if="alternatives.length > 0"
        type="warning"
        class="d-md-none alternatives-disclaimer"
        variant="tonal"
        prominent
      >
        <div>
          {{ $t('cart.alternatives.disclaimer.title') }}&nbsp;&nbsp;<strong style="vertical-align: middle;">&#9432;</strong>
        </div>
        <v-tooltip
          activator="parent"
          open-on-click
          location="bottom"
          max-width="450"
        >
          {{ $t('cart.alternatives.disclaimer.description') }}
        </v-tooltip>
      </v-alert>
      <div v-if="alternativeStore.isLoading" class="d-flex justify-center align-center py-8">
        <v-progress-circular size="100" indeterminate color="#6E31E8" />
      </div>
      <v-expansion-panels
        v-else-if="alternatives.length > 0"
        v-model="expandedIndices"
        data-test-id="alternatives-groups"
        variant="accordion"
        rounded="0"
        focusable
        multiple
      >
        <v-expansion-panel
          v-for="(groups, index) in alternatives"
          :key="index"
          ref="panels"
          :data-test-id="`alternatives-group-${index}`"
        >
          <v-expansion-panel-title color="#faf9fc" class="text-vm-purple">
            <i18n-t
              v-if="getMatchingCriteriaData(groups.matchingCriteria, props.alternativeProducts)"
              :keypath="getMatchingCriteriaData(groups.matchingCriteria, props.alternativeProducts)?.key"
              scope="global"
            >
              <template
                v-for="(value, key) in getMatchingCriteriaData(groups.matchingCriteria, props.alternativeProducts).params"
                :key="key"
                #[key]
              >
                <template v-if="Array.isArray(value)">
                  <template v-for="(item, i) in value" :key="i">
                    <v-chip
                      size="small"
                      class="ml-1"
                      variant="outlined"
                      color="#6E31E8"
                    >
                      {{ key === 'atcvetcode' || key === 'atcvetcodeRemoved2Chars' ? item : $t(item) }}
                    </v-chip>
                    <span v-if="i < value.length - 1" class="ml-1">
                      {{ key === 'speciesMin1' ? ` ${$t('or')} ` : ` ${$t('and')} ` }}
                    </span>
                  </template>
                </template>
                <template v-else>
                  {{ value }}
                </template>
              </template>
            </i18n-t>
            <template v-else>
              {{ groups.matchingCriteria }}
            </template>
            <template #actions="{ expanded }">
              <v-icon size="x-large" :icon="expanded ? IconChevronUp : IconChevronDown" />
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text v-for="(productGroup, i) in groups.productGroups" :key="i">
            <ProductGroup :product-group="productGroup" :list-index="(groupOffsets[index] ?? 0) + i" class="mt-4" />
            <v-divider v-if="i < groups.productGroups.length - 1" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-sheet>
  </v-bottom-sheet>
</template>

<style scoped lang="scss">
.alternatives-disclaimer {
  :deep(.v-alert__prepend i) {
    font-size: 26px !important;
  }
}
</style>
